import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { FormControl, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValid, parseISO, format } from "date-fns";
import { useCookies } from "react-cookie";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import CatQuickSelection from "./CatQuickSelection";
import CatSummary from "./CatSummary";
import PaymentModal from "../../../components/Payment/PaymentModal";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// helpers
import { dateIcon } from "../../../common/vector";
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
import { buildDefaultBookingUnitsQuantities } from "./helpers/bookingUnits";
import { getInitialTimePlusMinutes } from "./helpers/getInitialTimePlusMinutes";
import { WIEN_MITTE_STATION } from "./helpers/wienMitteStation";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  readAirlineTransportAddressesFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { parseAirlineDepartureTime } from "../../../common/utils/helpers/parseAirlineDepartureTime";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { catActionCreators } from "./catModule";
import { errorFieldActionCreators } from "../../../common/store/modules/errorFieldModule";
// types
import { type CatOffer, type CatOfferPrice } from "./types";

function pad2(v: number): string {
  return String(v).padStart(2, "0");
}

export default function Cat() {
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();

  const language = localStorage.getItem("i18nextLng") || "en";

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);
  const transportContext = useMemo(
    () => resolveTransportAirlineApiContext(restoredNavState ?? location.state),
    [restoredNavState, location.state]
  );
  const transportAddresses = useMemo(
    () => readAirlineTransportAddressesFromNavigationState(restoredNavState ?? location.state),
    [restoredNavState, location.state]
  );

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const catInitial = useSelector((state: any) => state.cat.catInitial);
  const catInitialError = useSelector((state: any) => state.cat.catInitialError);
  const selectedOffer = useSelector((state: any) => state.cat.selectedOffer);
  const bookingUnitsQuantities = useSelector((state: any) => state.cat.bookingUnitsQuantities) as Record<
    number,
    number
  >;
  const showCatPaymentModal = useSelector((state: any) => state.cat.showPaymentModal);
  const catBookingResponseData = useSelector((state: any) => state.cat.bookingResponseData);

  // actions
  const getCatOffers = useActions(catActionCreators?.getCatOffersAction, []);
  const getCatEstimation = useActions(catActionCreators?.getCatEstimationAction, []);
  const setSelectedOffer = useActions(catActionCreators?.setSelectedOfferAction, []);
  const setBookingUnitsQuantities = useActions(catActionCreators?.setBookingUnitsQuantitiesAction, []);
  const buyCatTickets = useActions(catActionCreators?.buyTicketsAction, []);
  const toggleCatPaymentModal = useActions(catActionCreators?.togglePaymentModalAction, []);
  const resetCatState = useActions(catActionCreators?.resetCatStateAction, []);
  const getCatLastMileRoute = useActions(catActionCreators?.getCatLastMileRouteAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  const lastInitialFetchKeyRef = useRef<string | null>(null);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const initialTime = useMemo(() => getInitialTimePlusMinutes(30), []);
  const [catHours, setCatHours] = useState<number>(initialTime.hours);
  const [catMinutes, setCatMinutes] = useState<number>(initialTime.minutes);

  // AIRLINE EXTENSION: prefill date & time from navigation state if present
  const airlineNav = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as
      | {
          meta?: { source?: string };
          shared?: { departureDate?: string; time?: string };
        }
      | null
      | undefined;

    if (nav?.meta?.source !== "airline") {
      return {
        isAirlineSource: false,
        departureDate: undefined as string | undefined,
        time: undefined as string | undefined
      };
    }
    return { isAirlineSource: true, departureDate: nav.shared?.departureDate, time: nav.shared?.time };
  }, [location.state, restoredNavState]);

  useEffect(() => {
    if (!airlineNav.isAirlineSource) return;

    if (airlineNav.departureDate) {
      const parsed = parseISO(airlineNav.departureDate);
      if (isValid(parsed)) {
        setSelectedDate(parsed);
      }
    }

    const parsedTime = parseAirlineDepartureTime(airlineNav.time);
    if (parsedTime) {
      setCatHours(parsedTime.hours);
      setCatMinutes(parsedTime.minutes);
    }
  }, [airlineNav]);

  useEffect(() => {
    return () => {
      toggleCatPaymentModal(false);
      resetCatState();
      resetErrorField();
      clearPersistedAirlineRouterState();
    };
  }, [toggleCatPaymentModal, resetCatState, resetErrorField]);

  const selectedDateKey = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);
  const estimationTime = useMemo(() => `${pad2(catHours)}:${pad2(catMinutes)}`, [catHours, catMinutes]);
  const bookingUnitsQuantitiesKey = useMemo(() => JSON.stringify(bookingUnitsQuantities), [bookingUnitsQuantities]);

  const { departureAddress, departureAddressId, destinationAddress, destinationAddressId } = transportAddresses;

  // --- LAST MILE ---
  const isAirlineTransferHub = transportContext.mode === "airline";
  const destinationPlaceId = destinationAddressId || null;
  const destinationName = destinationAddress || "";

  useEffect(() => {
    if (!isAirlineTransferHub || !destinationPlaceId) {
      return;
    }

    getCatLastMileRoute({
      departureLatitude: WIEN_MITTE_STATION.latitude,
      departureLongitude: WIEN_MITTE_STATION.longitude,
      destinationPlaceId,
      travelMode: "walking"
    });
  }, [isAirlineTransferHub, destinationPlaceId, getCatLastMileRoute]);
  // --- END OF LAST MILE ---

  useEffect(() => {
    if (!catInitial || !selectedOffer) return;

    getCatEstimation(
      {
        date: selectedDateKey,
        time: estimationTime,
        departureAddress,
        departureAddressId,
        destinationAddress,
        destinationAddressId
      },
      transportContext
    );
  }, [
    catInitial,
    selectedOffer,
    selectedOffer?.offer_code,
    bookingUnitsQuantitiesKey,
    selectedDateKey,
    estimationTime,
    departureAddress,
    departureAddressId,
    destinationAddress,
    destinationAddressId,
    transportContext,
    getCatEstimation
  ]);

  useEffect(() => {
    if (!tenantInfo) return;

    const fetchKey =
      transportContext.mode === "airline"
        ? `airline:${transportContext.iataCode}`
        : transportContext.mode === "airline_missing_iata"
        ? "airline:missing_iata"
        : "hotel";

    if (lastInitialFetchKeyRef.current === fetchKey) return;
    lastInitialFetchKeyRef.current = fetchKey;

    getCatOffers(transportContext);
  }, [tenantInfo, getCatOffers, transportContext]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/cat",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportContext.mode === "airline" ? transportContext.iataCode : null,
    onProviderLanguageChange: () => getCatOffers(transportContext)
  });

  const offers: Array<CatOffer> = useMemo(() => (catInitial?.items ?? []) as Array<CatOffer>, [catInitial]);

  const numberOptions = useMemo(() => Array.from({ length: 8 }, (_, i) => i), []);

  const onSelectOffer = useCallback(
    (offer: CatOffer) => {
      setSelectedOffer(offer);
      setBookingUnitsQuantities(buildDefaultBookingUnitsQuantities(offer));
    },
    [setSelectedOffer, setBookingUnitsQuantities]
  );

  const handleBuyTickets = useCallback(() => {
    const offer = selectedOffer ?? offers?.[0] ?? null;
    const prices = offer?.offer_prices;
    const hasPrices = Array.isArray(prices) && prices.length > 0;
    if (!hasPrices) return;

    const time = `${pad2(catHours)}:${pad2(catMinutes)}`;

    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: offer?.offer_code ?? null
    });

    buyCatTickets(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000),
        timeslot: time,
        offer_code: offer?.offer_code ?? null
      },
      navigate,
      transportContext
    );
  }, [
    buyCatTickets,
    cookies.cookieAccepted,
    platformType,
    tenantInfo,
    selectedDate,
    catHours,
    catMinutes,
    selectedOffer,
    offers,
    navigate,
    transportContext
  ]);

  return (
    <Grid
      className="cat grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      <TransportProviderHeader
        title={catInitial?.provider_title ?? catInitial?.title}
        fallbackTitle="CAT"
        titleClassName="cat__title"
        shortDescription={catInitial?.provider_description}
        providerLogo={catInitial?.provider_logo}
      />

      <Grid container>
        {/* LEFT SIDE */}
        <Grid item md={6} xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Box className="row-action" pb={{ xs: 5, sm: 3 }} pr={2}>
            <Grid
              item
              sm={4}
              xs={5}
              sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img src={dateIcon?.default} alt="date" />
                <Box pt={2} sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 }, textTransform: "uppercase" }}>
                  {t("transport.validFrom")}
                </Box>
              </Box>
            </Grid>
            <Grid item sm={8} xs={7} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={selectedDate}
                    setSelectedDate={date => setSelectedDate(date ?? new Date())}
                    minDate={new Date()}
                    dataAttribute="cat-date"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* RIGHT SIDE */}
        <Grid item md={6} xs={12} mt={{ sm: 6, xs: 6, md: 0 }} position={"relative"}>
          <div className="vertical-divider" />
          <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
            <CatQuickSelection offers={offers} selectedOffer={selectedOffer} onSelectOffer={onSelectOffer} />

            {/* PASSENGERS (booking units) */}
            <Box
              sx={{ textTransform: "uppercase", display: "flex", gap: 2 }}
              mt={5}
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
            >
              {selectedOffer?.offer_prices?.length > 0 &&
                selectedOffer?.offer_prices?.map((unit: CatOfferPrice) => (
                  <Box key={unit.id} sx={{ flex: 1, minWidth: 206 }}>
                    <Box pb={1} sx={{ textAlign: "left", fontSize: { xs: 12, sm: 13 } }}>
                      {unit.description}
                    </Box>
                    <FormControl fullWidth>
                      <Select
                        value={bookingUnitsQuantities?.[unit.id] ?? 0}
                        onChange={e =>
                          setBookingUnitsQuantities({
                            ...bookingUnitsQuantities,
                            [unit.id]: Number(e.target.value)
                          })
                        }
                        input={<OutlinedInput data-cy={`cat-passengers-input-${unit.id}`} />}
                        renderValue={value => String(value)}
                        data-cy={`cat-passengers-${unit.id}`}
                      >
                        {numberOptions.map(n => (
                          <MenuItem key={`${unit.id}-${n}`} value={n} data-cy={`cat-passengers-${unit.id}-${n}`}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
            </Box>

            {catInitialError && (
              <Box mt={1} className="cat__error-msg">
                {t("transport.prebookCustomError")}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid item xs={12} mt={{ xs: 2 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
        {selectedOffer && (
          <CatSummary
            onBuyTickets={handleBuyTickets}
            cancellationPolicy={selectedOffer?.cancellation_policy || catInitial?.cancellation_policy}
            lastMileDestinationName={isAirlineTransferHub ? destinationName : null}
          />
        )}
      </Grid>

      {showCatPaymentModal && (
        <PaymentModal
          isOpen={showCatPaymentModal}
          bookingResponseData={catBookingResponseData}
          togglePaymentModal={toggleCatPaymentModal}
          redirectUrl="transport/cat/confirmation"
        />
      )}
    </Grid>
  );
}
